import * as React from "react";

import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  useQuickSearchHotkey,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  type QuickSearchProperty,
} from "@/shared/components/ResourceView";
import {
  sessionTopicDurationNs,
  sessionTopicFrequencyHz,
  type SessionTopicRecord,
} from "@/shared/domain/sessions";
import { useAllSessionTopics } from "@/shared/domain/sessions/hooks";
import { Banner } from "@/shared/foundation/primitives";
import {
  cellStyles,
  colId,
  ColumnVisibility,
  createColumnHelper,
  DataTable,
  deriveLoadingState,
  FLEX_FILL_COL_DEF,
  renderDashOrValue,
  TimestampCell,
  timestampCellClass,
  useInMemoryRows,
  type ColDef,
  type ColumnPickerOption,
  type ICellRendererParams,
  type InMemorySortKey,
} from "@/shared/foundation/primitives/Table";
import { userFacingErrorMessage } from "@/shared/http";
import { isSubstringMatch } from "@/shared/text/substringFilter";
import { formatShortNanoDuration } from "@/shared/time/formatDuration";
import { nanoSecToDate } from "@/shared/time/time";

import { PendingStat } from "./PendingStat";
import { SessionTopicPanel } from "./SessionTopicPanel";
import { TopicStatsRunStatus } from "./TopicStatsRunStatus";
import { useSessionOutletContext } from "./useSessionOutletContext";
import { useTopicStatsRunState } from "./useTopicStatsRunState";

const SCHEMA_FIELD = "schema";

/** The Schema column: the topic's schema, or how many it is recorded with. */
function schemaLabel({ schemas }: SessionTopicRecord): string | null {
  if (schemas.length === 0) {
    return null;
  }
  return schemas.length === 1 ? schemas[0].name : `${schemas.length} schemas`;
}

function nsToDate(ns: bigint | null): Date | null {
  return ns === null ? null : nanoSecToDate(ns);
}

const countFormat = new Intl.NumberFormat();
const rateFormat = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});
const slowRateFormat = new Intl.NumberFormat(undefined, {
  maximumSignificantDigits: 2,
});

function formatFrequency(hz: number): string {
  return `${(hz < 1 ? slowRateFormat : rateFormat).format(hz)} Hz`;
}

/**
 * A row of the table: a topic, and whether a topic stats run is under way that
 * will publish its message count. The flag rides on the row so the stat cells
 * repaint when the run starts or stops.
 */
interface TopicRow extends SessionTopicRecord {
  statsPending: boolean;
}

/** A stat column's cell: a placeholder while a run will publish it, else its value or a dash. */
function renderStat(params: ICellRendererParams<TopicRow>) {
  return params.data?.statsPending ? (
    <PendingStat />
  ) : (
    renderDashOrValue(params)
  );
}

const columnHelper = createColumnHelper<TopicRow>();

const COLUMNS: ColDef<TopicRow>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 200,
    sortable: true,
    cellClass: cellStyles.primaryContent,
  }),
  columnHelper.accessor(schemaLabel, {
    colId: SCHEMA_FIELD,
    headerName: "Schema",
    flex: 1.5,
    minWidth: 160,
    sortable: true,
    cellClass: cellStyles.secondaryContent,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor(sessionTopicDurationNs, {
    colId: "duration",
    headerName: "Duration",
    minWidth: 110,
    sortable: true,
    valueFormatter: (params) => formatShortNanoDuration(params.value),
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor((topic) => nsToDate(topic.start_time_ns), {
    colId: "start",
    headerName: "Start",
    minWidth: 160,
    sortable: true,
    cellRenderer: TimestampCell,
    cellClass: timestampCellClass,
  }),
  columnHelper.accessor((topic) => nsToDate(topic.end_time_ns), {
    colId: "end",
    headerName: "End",
    minWidth: 160,
    sortable: true,
    cellRenderer: TimestampCell,
    cellClass: timestampCellClass,
  }),
  columnHelper.accessor((topic) => topic.message_count, {
    colId: "messages",
    type: "rightAligned",
    headerName: "Messages",
    minWidth: 110,
    sortable: true,
    valueFormatter: (params) =>
      params.value === null || params.value === undefined
        ? ""
        : countFormat.format(params.value),
    cellRenderer: renderStat,
  }),
  columnHelper.accessor(sessionTopicFrequencyHz, {
    colId: "frequency",
    type: "rightAligned",
    headerName: "Frequency",
    minWidth: 110,
    sortable: true,
    valueFormatter: (params) =>
      params.value === null || params.value === undefined
        ? ""
        : formatFrequency(params.value),
    cellRenderer: renderStat,
  }),
  columnHelper.accessor("topic_id", {
    headerName: "Topic ID",
    minWidth: 180,
  }),
];

/** The columns that come from the published `message_count` stat. */
const STATS_COLUMN_IDS = new Set(["messages", "frequency"]);

/**
 * Topic ID is offered in the Columns menu but starts hidden. The stats columns
 * are seeded visible even while the flag is off, so they show when the flag
 * finishes loading after the tab mounts.
 */
const DEFAULT_VISIBLE_COLUMN_IDS = COLUMNS.map(colId).filter(
  (id) => id !== "topic_id",
);

interface ColumnSet {
  columns: ColDef<TopicRow>[];
  pickerOptions: ColumnPickerOption[];
}

function columnSet(columns: ColDef<TopicRow>[]): ColumnSet {
  return {
    columns,
    pickerOptions: columns.map((column) => ({
      id: colId(column),
      label: column.headerName ?? colId(column),
    })),
  };
}

const WITH_STATS = columnSet(COLUMNS);
const WITHOUT_STATS = columnSet(
  COLUMNS.filter((column) => !STATS_COLUMN_IDS.has(colId(column))),
);

/**
 * How each sortable column compares two topics. The listing takes no sort
 * parameter and `<DataTable>` sorts server-side by contract, so the rows are
 * sorted here; keep this map and the `sortable: true` flags in step.
 */
const SORT_KEYS: Record<string, InMemorySortKey<TopicRow>> = {
  name: (topic) => topic.name,
  [SCHEMA_FIELD]: schemaLabel,
  duration: (topic) => {
    const duration = sessionTopicDurationNs(topic);
    return duration === null ? null : Number(duration);
  },
  start: (topic) => nsToDate(topic.start_time_ns),
  end: (topic) => nsToDate(topic.end_time_ns),
  messages: (topic) => topic.message_count,
  frequency: sessionTopicFrequencyHz,
};

// Quick find searches topic names only: topics have no id-shaped value worth
// routing elsewhere.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "name", name: "Name", fallback: true },
];

/** The quick-find chip's definition, so editing it keeps a name comparator. */
const NAME_FILTER_DEFINITION: FilterDefinition = {
  type: "string",
  field: "name",
  name: "Name",
  allowedComparators: ["CONTAINS", "NOT_CONTAINS"],
};

/**
 * Whether `topic` passes `filter`. A name filter keeps a topic whose name
 * contains (or, for "does not contain", contains none of) its text. The
 * schema filter's "contains" keeps a topic recorded with any of the chosen
 * schemas, "does not contain" one recorded with none of them. A filter on any
 * other field keeps every topic.
 */
function matchesFilter(topic: SessionTopicRecord, filter: Filter): boolean {
  if (filter.type === "string" && filter.field === "name") {
    const named = filter.values.some((value) =>
      isSubstringMatch(value.trim(), topic.name),
    );
    return filter.comparator === "NOT_CONTAINS" ? !named : named;
  }
  if (filter.field !== SCHEMA_FIELD || filter.type !== "set") {
    return true;
  }
  const chosen = new Set(filter.values.map((option) => option.value));
  if (chosen.size === 0) {
    return true;
  }
  const recordedWithChosen = topic.schemas.some(
    (schema) => schema.name !== null && chosen.has(schema.name),
  );
  return filter.comparator === "NOT_CONTAINS"
    ? !recordedWithChosen
    : recordedWithChosen;
}

function matchesFilters(
  topic: SessionTopicRecord,
  filters: readonly Filter[],
  matchMode: FilterMatchMode,
): boolean {
  if (filters.length === 0) {
    return true;
  }
  return matchMode === "OR"
    ? filters.some((filter) => matchesFilter(topic, filter))
    : filters.every((filter) => matchesFilter(topic, filter));
}

/**
 * Topics tab of the session-detail page. Lists every topic whose data the
 * session takes, with its schema, time span in the session, message count and
 * frequency, searchable by name and filterable by schema. Expanding a row
 * lists the topic's fields and their stats. Message count, frequency, the
 * field stats and the state of the latest topic stats run appear only while
 * `release-session-topic-stats` is on for the session's org; while a run is
 * under way the toolbar says so and placeholders stand where its values will
 * appear.
 *
 * The listing has no sort or filter parameters, so every page of it is
 * fetched and the rows are filtered, sorted and paged here.
 */
export function TopicsTab() {
  const { session } = useSessionOutletContext();
  const query = useAllSessionTopics(session.session_id);
  const { statsShown, run, generating } = useTopicStatsRunState(session);
  const { columns, pickerOptions } = statsShown ? WITH_STATS : WITHOUT_STATS;

  const view = useResourceView<TopicRow>({
    getRowId: (topic) => topic.topic_id,
    initialSort: { columnId: "name", direction: "asc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  const topics = React.useMemo(
    () =>
      (query.data ?? []).map((topic): TopicRow => ({
        ...topic,
        statsPending: generating,
      })),
    [query.data, generating],
  );

  const filterDefinitions = React.useMemo<FilterDefinition[]>(() => {
    const schemaNames = new Set(
      topics.flatMap((topic) =>
        topic.schemas.flatMap((schema) =>
          schema.name === null ? [] : [schema.name],
        ),
      ),
    );
    return [
      {
        type: "set",
        field: SCHEMA_FIELD,
        name: "Schema",
        options: [...schemaNames].sort((a, b) => a.localeCompare(b)),
      },
    ];
  }, [topics]);

  const activeFilterDefinitions = React.useMemo(
    () => [...filterDefinitions, NAME_FILTER_DEFINITION],
    [filterDefinitions],
  );
  const quickSearch = useQuickSearchHotkey();

  const filteredTopics = React.useMemo(() => {
    if (view.filters.type !== "structured") {
      return topics;
    }
    const { filters, matchMode } = view.filters;
    return topics.filter((topic) => matchesFilters(topic, filters, matchMode));
  }, [topics, view.filters]);

  const { visibleRows, totalRowCount, hasNextPage } = useInMemoryRows(
    view.table,
    filteredTopics,
    SORT_KEYS,
  );

  const renderExpandedRow = React.useCallback(
    (record: TopicRow) => (
      <SessionTopicPanel
        topic={record}
        sessionId={session.session_id}
        orgId={session.org_id}
        statsShown={statsShown}
        generating={generating}
      />
    ),
    [session.session_id, session.org_id, statsShown, generating],
  );

  if (query.isError) {
    return (
      <Banner variant="error">
        {userFacingErrorMessage(
          query.error,
          <>
            Failed to load topics. Please contact{" "}
            <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
            problem persists.
          </>,
        )}
      </Banner>
    );
  }

  return (
    <ResourceView.Root>
      {statsShown && run.error !== null && (
        <Banner variant="error">
          {userFacingErrorMessage(
            run.error,
            "Failed to check the latest topic stats run.",
          )}
        </Banner>
      )}
      <QuickSearch
        open={quickSearch.isOpen}
        onClose={quickSearch.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder="Find topics by name"
        onSubmit={(filter) => view.commit({ type: "structured", filter })}
      />
      <ResourceView.Actions>
        <QuickSearchButton onClick={quickSearch.open} label="Find topics" />
        <ResourceView.Filters
          filterDefinitions={filterDefinitions}
          onFilterSelected={view.commit}
        />
        <ColumnVisibility
          options={pickerOptions}
          visibleColumnIds={view.table.visibleColumnIds}
          setColumnVisibility={view.table.setColumnVisibility}
        />
        {statsShown && run.data !== undefined && (
          <TopicStatsRunStatus run={run.data} />
        )}
      </ResourceView.Actions>
      <ResourceView.ActiveFilters
        filters={view.filters}
        filterDefinitions={activeFilterDefinitions}
        onRemove={view.removeFilter}
        onUpdate={view.updateFilter}
        onClear={view.clearFilters}
        onMatchModeChange={view.setMatchMode}
        onFilterSelected={view.commit}
      />
      <DataTable<TopicRow>
        defaultColDef={FLEX_FILL_COL_DEF}
        table={view.table}
        columns={columns}
        data={visibleRows}
        totalRowCount={totalRowCount}
        hasNextPage={hasNextPage}
        loading={deriveLoadingState(query)}
        noRowsContent={
          view.hasActiveFilters
            ? "No topics match your search or filters"
            : "No topics in this session yet"
        }
        expansion={{ renderRow: renderExpandedRow }}
      />
    </ResourceView.Root>
  );
}
